import React, { useEffect, useCallback, useRef, useState } from "react";
import SectionContent from "../Fragments/SectionContent";
import { usePublication } from "../../../hooks/usePublication";
import { ButtonsRight } from "../../../interfaces/UI";
import MenuRightSection from "./MenuRightSection";
import MenuLeftSection from "./MenuLeftSection";
import CoverPage from "../Fragments/CoverPage";
import FooterBook from "./FooterBook";
import Pager from "./Pager";
import { useLocation, useParams } from "react-router-dom";
import { FloatingPodcast } from "../Fragments/FloatingPodcast";
import { ModalTrivia } from "../RightMenu";

interface MidSectionProps {
  viewButtons: ButtonsRight;
  openButtons: ButtonsRight;
  setOpenButtons: (state: ButtonsRight) => void;
}

const MidSection = ({
  viewButtons,
  openButtons,
  setOpenButtons,
}: MidSectionProps) => {
  const { publication } = usePublication();

  const { idCapitulo, idElementHTML } = useParams();
  const location = useLocation();
  const scrollRetryRef = useRef<NodeJS.Timeout | null>(null);
  const activeAnchorRef = useRef<string | null>(null);
  const maxRetriesRef = useRef<number>(15);

  // Función para verificar si el anchor está visible en pantalla
  const isAnchorVisible = useCallback((anchorName: string): boolean => {
    const escapedAnchorName = CSS.escape(anchorName);

    let element = document.querySelector(`a[name="${escapedAnchorName}"]`);
    if (!element) {
      element = document.getElementById(anchorName);
    }
    if (!element) {
      element = document.querySelector(
        `mark[data-hl-id="${escapedAnchorName}"]`
      );
    }
    if (!element) {
      element =
        document.querySelector(`a[name="${escapedAnchorName}"]`) ||
        document.getElementById(anchorName) ||
        document.querySelector(`mark[data-hl-id="${escapedAnchorName}"]`);
    }

    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Elemento en el 20%-80% del viewport
    const isWellPositioned =
      rect.top >= viewportHeight * 0.2 && rect.bottom <= viewportHeight * 0.8;

    // CRITERIO DE VISIBILIDAD BÁSICA
    const isInViewport = rect.top >= 0 && rect.bottom <= viewportHeight;

    // CRITERIO DE PROXIMIDAD
    const isNearViewport =
      rect.top >= -50 && rect.bottom <= viewportHeight + 50;

    // Usar criterio más flexible
    return isWellPositioned || isInViewport || isNearViewport;
  }, []);

  // Función para el scroll entre subsecciones
  const scrollToAnchorWithRetry = useCallback(
    (anchorName: string) => {
      if (scrollRetryRef.current) {
        clearTimeout(scrollRetryRef.current);
      }

      activeAnchorRef.current = anchorName;
      let retryCount = 0;
      const maxRetries = maxRetriesRef.current;
      let scrollCompleted = false;

      const attemptScroll = () => {
        // SI YA SE COMPLETÓ EL SCROLL, NO HACER NADA
        if (scrollCompleted) {
          return;
        }

        const escapedAnchorName = CSS.escape(anchorName);

        let element = document.querySelector(`a[name="${escapedAnchorName}"]`);
        if (!element) {
          element = document.getElementById(anchorName);
        }
        if (!element) {
          element = document.querySelector(
            `mark[data-hl-id="${escapedAnchorName}"]`
          );
        }
        if (!element) {
          element =
            document.querySelector(`a[name="${escapedAnchorName}"]`) ||
            document.getElementById(anchorName) ||
            document.querySelector(`mark[data-hl-id="${escapedAnchorName}"]`);
        }

        if (element) {
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // CRITERIO MÁS CONSERVADOR: Elemento debe estar bien centrado
          const isWellPositioned =
            rect.top >= viewportHeight * 0.35 &&
            rect.bottom <= viewportHeight * 0.65;

          if (isWellPositioned) {
            scrollCompleted = true;
            activeAnchorRef.current = null;
            if (scrollRetryRef.current) {
              clearTimeout(scrollRetryRef.current);
            }
            return;
          }

          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          // PROGRAMAR PRÓXIMO INTENTO CON MEJOR SINCRONIZACIÓN
          retryCount++;
          if (retryCount < maxRetries) {
            scrollRetryRef.current = setTimeout(attemptScroll, 500); // Aumentar delay
          } else {
            scrollCompleted = true;
            activeAnchorRef.current = null;
          }
        } else if (retryCount < maxRetries) {
          retryCount++;
          scrollRetryRef.current = setTimeout(attemptScroll, 400);
        } else {
          scrollCompleted = true;
          activeAnchorRef.current = null;
        }
      };

      attemptScroll();

      // Limpiar timeout si el componente se desmonta
      return () => {
        if (scrollRetryRef.current) {
          clearTimeout(scrollRetryRef.current);
        }
      };
    },
    [isAnchorVisible]
  );

  // Escuchar eventos de scroll
  useEffect(() => {
    const handleScroll = () => {
      if (activeAnchorRef.current) {
        if (isAnchorVisible(activeAnchorRef.current)) {
          if (scrollRetryRef.current) {
            clearTimeout(scrollRetryRef.current);
          }
          activeAnchorRef.current = null;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAnchorVisible]);

  // Escuchar eventos personalizados para scroll
  useEffect(() => {
    const handleScrollToAnchor = (event: CustomEvent) => {
      const { anchorName } = event.detail;

      // EVITAR MÚLTIPLES SCROLLS SIMULTÁNEOS
      if (activeAnchorRef.current === anchorName) {
        return;
      }


      // LIMPIAR CUALQUIER SCROLL ANTERIOR
      if (scrollRetryRef.current) {
        clearTimeout(scrollRetryRef.current);
        scrollRetryRef.current = null;
      }

      scrollToAnchorWithRetry(anchorName);
    };

    window.addEventListener(
      "scrollToAnchor",
      handleScrollToAnchor as EventListener
    );
    return () => {
      window.removeEventListener(
        "scrollToAnchor",
        handleScrollToAnchor as EventListener
      );
    };
  }, [scrollToAnchorWithRetry]);

  // Scroll automático cuando hay hash en la URL (PRINCIPAL)
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.startsWith("#C")) {
      const anchorName = hash.substring(1);

      // Esperar a que React Router haya cambiado el capítulo y el contenido se haya renderizado
      const timeoutId = setTimeout(() => {
        scrollToAnchorWithRetry(anchorName);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, scrollToAnchorWithRetry]);

  // También escuchar cambios de ubicación completa (por si acaso)
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.startsWith("#C")) {
      const anchorName = hash.substring(1);

      const timeoutId = setTimeout(() => {
        scrollToAnchorWithRetry(anchorName);
      }, 600);

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, location.hash, scrollToAnchorWithRetry]);

  useEffect(() => {
    const container = document.getElementById("mainContent");
    if (!container || location.hash) return;
    container.scrollTo({ top: 0, behavior: "auto" });
  }, [idCapitulo, location]);

  useEffect(() => {
    if (!idElementHTML) return;
    const container = document.getElementById(idElementHTML);
    if (!container || location.hash) return;
    container.scrollTo({ top: 0, behavior: "auto" });
  }, [idElementHTML, location]);

  const movePage = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    
  }, []);

  const toggleMenuMovil = () => {
    setOpenButtons({
      ...openButtons,
      optIndex: !openButtons.optIndex,
    });
  };

  return (
    <>
      <div className="l1-wrapper__mid gui">
        <div className="l1-wrapper__mid-ov"></div>

        <div
          className="content main-sb"
          id="mainContent"
          data-step="218,165,32"
          onScroll={movePage}
        >
          {publication.currentSection === 0 && (
            <CoverPage
              openButtons={openButtons}
              setOpenButtons={setOpenButtons}
            />
          )}

          <main id="start" className="content__page content-width snap">
            {publication.currentSection >= 0 &&
              publication.sections[publication.currentSection] && (
                <SectionContent
                  section={publication.sections[publication.currentSection]}
                  openButtons={openButtons}
                  setOpenButtons={setOpenButtons}
                />
              )}
          </main>

          <FooterBook />
          <Pager />
        </div>

        <MenuLeftSection
          viewButtons={viewButtons}
          openButtons={openButtons}
          setOpenButtons={setOpenButtons}
        />
        <MenuRightSection
          viewButtons={viewButtons}
          openButtons={openButtons}
          setOpenButtons={setOpenButtons}
        />

        <div id="gb-bottom" className="gui__group gui__group--bottom">
          <div className="gui__subgroup gui__subgroup--br">
            <button
              type="button"
              id="group-btn"
              className="btn btn-rounded btn-rounded-primary aside-btn tippy"
              data-tippy-content="Menú"
              onClick={toggleMenuMovil}
              aria-label="Opciones móvil"
            >
              <span className="btn__icon">
                <i className="icon icon-vpro-more-options-vertical"></i>
              </span>
            </button>
          </div>
        </div>

        <ModalTrivia />
        <div className="backdrop js-backdrop"></div>
      </div>
    </>
  );
};

export default MidSection;
